import { MaskLine, Rise } from "@/components/motion/Reveal";
import { ArrowUpRight } from "lucide-react";
import { useState } from "react";
import { Toaster } from "sonner";
import Seo from "@/seo/Seo";
import { ldBreadcrumb } from "@/seo/seoConfig";
import { ApplyForm } from "@/components/careers/ApplyForm";
import { DIRECT_EMAIL } from "@/components/layout/ConsultPopover";
import { useLang } from "@/i18n/LangContext";

const ROLES = [
  {
    slug: "legal-associate-uae",
    title: "Legal Associate — UAE",
    type: "Full Time",
    loc: "Dubai / RAKEZ",
    exp: "2–4 years",
    focus: "Corporate, Data Privacy & VARA-adjacent advisory",
    body: [
      "Support the founding partners on UAE regulatory advisory across DIFC, ADGM, VARA, SCA and PDPL mandates.",
      "Draft and review commercial contracts, term sheets, SSAs, SHAs, SAFEs and technology agreements.",
      "Coordinate with registered Emirati advocacy partners on litigation and formal representation.",
      "Publish short-form insights on emerging UAE regulation for the RPLC UAE journal.",
    ],
    requirements: [
      "UAE-recognised LL.B. (LL.M. preferred)",
      "2–4 years of post-qualification experience",
      "Strong drafting; English essential, Arabic advantageous",
      "Comfort with technology / financial services subject matter",
    ],
  },
  {
    slug: "litigation-associate-pune",
    title: "Litigation Associate — Pune",
    type: "Full Time",
    loc: "Pune",
    exp: "3–5 years",
    focus: "Criminal & Cyber Law",
    body: [
      "Independently draft pleadings, appear before Pune-based fora and coordinate with counsel in mofussil courts.",
      "Support Cyber Cell and Economic Offences Wing matters — from FIR stage through to Sessions and High Court.",
      "Prepare research memos on the DPDP Act, IT Act, IPC/BNS and cross-border cybercrime cooperation frameworks.",
    ],
    requirements: [
      "3–5 years of active courtroom experience",
      "Comfort with cyber crime, IT Act and criminal procedure",
      "Excellent drafting and oral advocacy",
      "Enrolled with a Bar Council of India state bar",
    ],
  },
  {
    slug: "corporate-associate-pune",
    title: "Corporate Associate — Pune",
    type: "Full Time",
    loc: "Pune",
    exp: "3–5 years",
    focus: "Corporate & Commercial Law",
    body: [
      "Lead corporate advisory across venture funding, FEMA/FDI, M&A, joint ventures and IP portfolios.",
      "Manage transaction closings — from term sheet to CP satisfaction — for India and cross-border deals.",
      "Support GC-retainer clients on day-to-day contracting, policy drafting and board reporting.",
    ],
    requirements: [
      "3–5 years of corporate transactions experience",
      "Strong exposure to VC/PE documentation",
      "Cross-border deal awareness (India–UAE / India–USA preferred)",
      "Enrolled with a Bar Council of India state bar",
    ],
  },
  {
    slug: "legal-intern",
    title: "Legal Intern",
    type: "Internship",
    loc: "Pune · Dubai (remote welcome)",
    exp: "1–3 months",
    focus: "Law students / recent graduates",
    body: [
      "Rotate across live matters in technology, data protection, corporate and cross-border advisory.",
      "Contribute to research memos, contract review, and short-form insights for the RPLC journal.",
      "Sit in on client calls (subject to conflicts) and observe deal negotiations end-to-end.",
    ],
    requirements: [
      "Current law student (3rd year+) or recent LL.B. / LL.M. graduate",
      "Strong writing and research fundamentals",
      "Genuine curiosity about technology & regulation",
      "Available for at least 4 continuous weeks",
    ],
  },
];

export default function CareersPage() {
  const { t } = useLang();
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);
  const [applying, setApplying] = useState(null);

  const filtered = ROLES.filter((r) => filter === "All" || r.type === filter);
  const generalRole = { slug: "general", title: t.careersForm.generalTitle, type: "General" };

  return (
    <div className="pt-40">
      <Toaster position="bottom-right" theme="dark" richColors />
      <Seo
        title="Careers · Legal Associate & Intern Openings in UAE and Pune"
        description="Join RPLC UAE — openings for Legal Associates in Dubai, Litigation & Corporate Associates in Pune, and Legal Interns. Modern subject matter across VARA, PDPL, DPDPA, AI governance and cross-border venture."
        path="/careers"
        keywords={["legal jobs Dubai", "law firm careers UAE", "lawyer jobs Pune", "cyber law internship India", "legal associate DIFC", "law careers RAKEZ", "corporate associate jobs Pune"]}
        jsonLd={ldBreadcrumb([{ name: "Home", path: "/" }, { name: "Careers", path: "/careers" }])}
      />
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Rise>
          <div className="flex items-center gap-4 mb-10">
            <span className="accent-bar" />
            <span className="eyebrow">Careers · UAE & Pune</span>
          </div>
        </Rise>
        <h1 className="font-serif tracking-tight leading-[0.9] text-[13vw] lg:text-[8vw]">
          <MaskLine delay={0.15}>Keen to work</MaskLine>
          <MaskLine delay={0.32}>and <span className="italic-accent">learn</span> with us?</MaskLine>
        </h1>
        <Rise delay={0.5}>
          <p className="mt-10 text-[#A19D94] text-lg max-w-3xl leading-relaxed">
            We don&rsquo;t just practise law, we build careers worth having. RPLC combines full-service legal work with a modern, technology-first outlook — inviting exceptional legal talent to grow with us across the UAE and Pune.
          </p>
        </Rise>
      </section>

      {/* CULTURE */}
      <section className="mt-32 border-y border-[#141414] py-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <Rise><div className="flex items-center gap-4 mb-8"><span className="accent-bar" /><span className="eyebrow">The culture</span></div></Rise>
            <Rise delay={0.1}>
              <h2 className="font-serif text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                Small bench. <span className="italic-accent">Wide horizon.</span>
              </h2>
            </Rise>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#141414] border border-[#141414]">
            {[
              { n: "01", t: "Ownership from day one", d: "Every associate carries meaningful matters. No file is buried behind seniority." },
              { n: "02", t: "Deep mentorship", d: "Weekly craft reviews with the founders on drafting, negotiation and oral advocacy." },
              { n: "03", t: "Modern subject matter", d: "AI governance, VARA, PDPL, DPDPA, cross-border venture — the practice sits at the frontier." },
              { n: "04", t: "Confidential process", d: "All applications reviewed in confidence. We respond to every candidate we take forward." },
            ].map((c) => (
              <div key={c.n} className="bg-[#050505] p-8 lg:p-10">
                <div className="chapter-num text-[#C84C35]">{c.n}</div>
                <div className="font-serif text-2xl lg:text-3xl mt-4 leading-tight">{c.t}</div>
                <p className="mt-4 text-[#A19D94] text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FILTER + ROLES */}
      <section className="py-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 mb-12">
            <div>
              <Rise><div className="flex items-center gap-4 mb-6"><span className="accent-bar" /><span className="eyebrow">Open positions · {ROLES.length}</span></div></Rise>
              <Rise delay={0.1}>
                <h2 className="font-serif text-5xl lg:text-6xl leading-[0.95] tracking-tight">Current openings.</h2>
              </Rise>
            </div>
            <Rise delay={0.15}>
              <div className="flex gap-2" data-testid="careers-filter">
                {["All", "Full Time", "Internship"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    data-testid={`careers-filter-${f.toLowerCase().replace(" ", "-")}`}
                    className={`chapter-num px-4 py-2 border transition-colors duration-500 ${filter === f ? "bg-[#F4F1EB] text-[#050505] border-[#F4F1EB]" : "border-[#222] text-[#A19D94] hover:border-[#F4F1EB] hover:text-[#F4F1EB]"}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </Rise>
          </div>

          <div className="border-t border-[#141414]">
            {filtered.map((r, i) => (
              <Rise key={r.slug} delay={i * 0.04}>
                <div className="border-b border-[#141414]">
                  <button
                    onClick={() => setOpen(open === r.slug ? null : r.slug)}
                    data-testid={`careers-role-${r.slug}`}
                    className="w-full text-left grid grid-cols-12 gap-4 items-start py-8 group hover:pl-6 transition-[padding] duration-700"
                  >
                    <div className="col-span-1 chapter-num pt-2">{String(i + 1).padStart(2, "0")}</div>
                    <div className="col-span-11 lg:col-span-6">
                      <h3 className="font-serif text-3xl lg:text-5xl leading-[1] tracking-tight group-hover:text-[#C84C35] transition-colors duration-700">{r.title}</h3>
                      <div className="mt-2 text-[#A19D94] text-sm">{r.focus}</div>
                    </div>
                    <div className="col-span-8 lg:col-span-4 flex flex-wrap gap-2">
                      <span className="chapter-num border border-[#222] px-2.5 py-1">{r.type}</span>
                      <span className="chapter-num border border-[#222] px-2.5 py-1">{r.loc}</span>
                      <span className="chapter-num border border-[#222] px-2.5 py-1">{r.exp}</span>
                    </div>
                    <div className="col-span-4 lg:col-span-1 flex justify-end text-[#66635C] group-hover:text-[#C84C35] transition-colors">
                      <ArrowUpRight size={22} strokeWidth={1.25} className={`transition-transform duration-500 ${open === r.slug ? "rotate-90" : ""}`} />
                    </div>
                  </button>

                  <div
                    style={{
                      maxHeight: open === r.slug ? (applying === r.slug ? 4000 : 1200) : 0,
                      opacity: open === r.slug ? 1 : 0,
                      transition: "max-height 0.8s cubic-bezier(0.76,0,0.24,1), opacity 0.6s ease",
                      overflow: "hidden",
                    }}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pb-12 pl-0 lg:pl-0">
                      <div className="lg:col-span-1"></div>
                      <div className="lg:col-span-6">
                        <div className="eyebrow mb-3">The role</div>
                        <ul className="space-y-3 text-[#F4F1EB] font-serif text-lg leading-relaxed">
                          {r.body.map((b, k) => (
                            <li key={k} className="flex gap-3">
                              <span className="chapter-num text-[#C84C35] pt-2">§{String(k + 1).padStart(2, "0")}</span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="lg:col-span-4">
                        <div className="eyebrow mb-3">What we look for</div>
                        <ul className="space-y-2 text-[#A19D94]">
                          {r.requirements.map((rq, k) => (
                            <li key={k} className="flex items-start gap-3">
                              <span className="w-1.5 h-1.5 bg-[#C84C35] mt-2.5 shrink-0" />
                              <span>{rq}</span>
                            </li>
                          ))}
                        </ul>
                        {applying !== r.slug && (
                          <button
                            type="button"
                            onClick={() => setApplying(r.slug)}
                            data-testid={`careers-apply-${r.slug}`}
                            className="btn-primary mt-8 inline-flex"
                          >
                            {t.careersForm.apply} · {r.type}
                            <ArrowUpRight size={14} strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                      {applying === r.slug && (
                        <div className="lg:col-span-10 lg:col-start-2">
                          <ApplyForm role={r} onClose={() => setApplying(null)} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* GENERAL */}
      <section className="py-24 border-t border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Rise>
              <h2 className="font-serif text-5xl lg:text-[6vw] leading-[0.95] tracking-tight">
                Don&rsquo;t see the <span className="italic-accent">right role?</span>
              </h2>
            </Rise>
            <Rise delay={0.1}>
              <p className="mt-6 text-[#A19D94] text-lg max-w-2xl leading-relaxed">
                Send us a note anyway. All applications are read by the founding partners, and reviewed in confidence.
              </p>
            </Rise>
          </div>
          <div className="lg:col-span-4 lg:justify-end flex">
            <Rise delay={0.15}>
              <button type="button" onClick={() => setApplying(applying === "general" ? null : "general")} className="btn-primary" data-testid="careers-general-apply">
                {t.careersForm.generalCta}
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </button>
            </Rise>
          </div>
        </div>
        {applying === "general" && (
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-12">
            <ApplyForm role={generalRole} onClose={() => setApplying(null)} />
          </div>
        )}
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-12 chapter-num text-[#66635C]">
          RPLC · Rane Pingle Law Chambers · All applications reviewed in confidence · <a href={`mailto:${DIRECT_EMAIL}`} className="link-hover text-[#A19D94]" data-testid="careers-direct-email">{DIRECT_EMAIL}</a>
        </div>
      </section>
    </div>
  );
}
